/**
 * Monthly Coupons tab — configure tier-based coupon auto-generation.
 *
 * Admins define which coupon template gets issued to which loyalty tier
 * (or customer group) each month. The scheduler creates the coupons
 * on the 1st of each month.
 *
 * Feature 3 — OPS-1838 (Monthly Coupon Auto-generation)
 */

import { useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  useMonthlyCouponConfig,
  useSaveMonthlyCouponConfig,
  useRewardsConfig,
  type TierCouponTemplate,
} from "../api/rewardsAdminHooks";
import { GeneratedCouponsList } from "./GeneratedCouponsList";

const EMPTY_ROW: TierCouponTemplate = {
  loyalty_tier: "",
  customer_group: "",
  coupon_template: "",
  coupons_per_month: 1,
  auto_gen_enabled: 1,
};

export function MonthlyCouponsTab() {
  const configQuery = useMonthlyCouponConfig();
  const saveMutation = useSaveMonthlyCouponConfig();
  const rewardsConfigQuery = useRewardsConfig();
  const tiers = rewardsConfigQuery.data?.tiers ?? [];

  const [localTemplates, setLocalTemplates] = useState<
    TierCouponTemplate[] | null
  >(null);
  const [localSchedule, setLocalSchedule] = useState<string | null>(null);

  // Derive display state: local edits take priority, else server data
  const templates =
    localTemplates ?? configQuery.data?.templates ?? [];
  const schedule =
    localSchedule ?? configQuery.data?.auto_gen_schedule ?? "None";

  const isDirty = localTemplates !== null || localSchedule !== null;

  const updateRow = useCallback(
    (idx: number, patch: Partial<TierCouponTemplate>) => {
      setLocalTemplates((prev) => {
        const base = prev ?? configQuery.data?.templates ?? [];
        const next = [...base];
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    },
    [configQuery.data],
  );

  const addRow = useCallback(() => {
    setLocalTemplates((prev) => {
      const base = prev ?? configQuery.data?.templates ?? [];
      return [...base, { ...EMPTY_ROW }];
    });
  }, [configQuery.data]);

  const removeRow = useCallback(
    (idx: number) => {
      setLocalTemplates((prev) => {
        const base = prev ?? configQuery.data?.templates ?? [];
        return base.filter((_, i) => i !== idx);
      });
    },
    [configQuery.data],
  );

  const handleSave = () => {
    saveMutation.mutate(
      {
        templates,
        auto_gen_schedule: schedule,
      },
      {
        onSuccess: () => {
          toast.success("บันทึกการตั้งค่าคูปองรายเดือนสำเร็จ");
          setLocalTemplates(null);
          setLocalSchedule(null);
        },
        onError: (err) =>
          toast.error(err.message || "เกิดข้อผิดพลาดในการบันทึก"),
      },
    );
  };

  if (configQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Schedule toggle */}
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <p className="text-sm font-medium">การสร้างคูปองอัตโนมัติ</p>
          <p className="text-xs text-muted-foreground">
            ระบบจะสร้างคูปองอัตโนมัติในวันที่ 1 ของทุกเดือน
          </p>
        </div>
        <Select
          value={schedule}
          onValueChange={(val) => setLocalSchedule(val)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="None">ปิด</SelectItem>
            <SelectItem value="Monthly">รายเดือน</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Template rows */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">เทมเพลตคูปองตามเทียร์</Label>
          <Button variant="outline" size="sm" onClick={addRow}>
            <Plus className="mr-1 h-3 w-3" />
            เพิ่ม
          </Button>
        </div>

        {templates.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <Ticket className="mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              ยังไม่มีเทมเพลตคูปอง กดปุ่ม &quot;เพิ่ม&quot; เพื่อเริ่มตั้งค่า
            </p>
          </div>
        )}

        {templates.map((row, idx) => (
          <div
            key={idx}
            className="grid grid-cols-[1fr_1fr_1fr_80px_48px_36px] items-end gap-2 rounded-lg border p-3"
          >
            {/* Loyalty Tier */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">เทียร์</Label>
              <Select
                value={row.loyalty_tier}
                onValueChange={(val) =>
                  updateRow(idx, { loyalty_tier: val })
                }
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="เลือกเทียร์" />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map((t) => (
                    <SelectItem key={t.tier_name} value={t.tier_name}>
                      {t.tier_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Group (fallback) */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                กลุ่มลูกค้า
              </Label>
              <Input
                placeholder="(ทางเลือก)"
                value={row.customer_group}
                onChange={(e) =>
                  updateRow(idx, { customer_group: e.target.value })
                }
                className="text-sm"
              />
            </div>

            {/* Coupon Template */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                เทมเพลตคูปอง
              </Label>
              <Input
                placeholder="Coupon Code name"
                value={row.coupon_template}
                onChange={(e) =>
                  updateRow(idx, { coupon_template: e.target.value })
                }
                className="text-sm"
              />
            </div>

            {/* Coupons per month */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">จำนวน/เดือน</Label>
              <Input
                type="number"
                min={1}
                value={row.coupons_per_month}
                onChange={(e) =>
                  updateRow(idx, {
                    coupons_per_month: Math.max(1, Number(e.target.value) || 1),
                  })
                }
                className="text-sm text-right"
              />
            </div>

            {/* Enabled toggle */}
            <div className="flex items-center justify-center pb-1">
              <Switch
                checked={!!row.auto_gen_enabled}
                onCheckedChange={(checked) =>
                  updateRow(idx, { auto_gen_enabled: checked ? 1 : 0 })
                }
              />
            </div>

            {/* Remove button */}
            <div className="flex items-center justify-center pb-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={() => removeRow(idx)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!isDirty || saveMutation.isPending}
        >
          {saveMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          บันทึก
        </Button>
      </div>

      {/* Generated coupons with expiry status */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
          คูปองที่สร้างแล้ว
        </p>
        <GeneratedCouponsList />
      </div>
    </div>
  );
}
